import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getVendorReports } from '@/features/vendor/api/commissions';
import {
    Activity,
    AlertCircle,
    ArrowUpRight,
    BarChart3,
    ChevronDown,
    Download,
    FileText,
    Info,
    PackageCheck,
    TrendingUp,
    Users,
    Wallet,
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2pdf from 'html2pdf.js';
import { getPackageDisplayName } from '@/lib/packageDisplay';
import { formatJalaliDate } from '@/lib/format';
import {
    formatReportCount,
    formatReportCurrency,
    formatReportPercent,
    getPlanProfitMetrics,
} from './reportMetrics';
import './VendorReportsPage.css';

const NET_PROFIT_FORMULA = 'درآمد − بهای تمام‌شده محصول − کارمزد پلتفرم − پورسانت معرفی پرداخت‌شده';
const exportDate = () => formatJalaliDate(new Date()).replaceAll('/', '-');

// 👈 [CHANGED] کامپوننت نمودار دایره‌ای با راهنمای گرید و بدون مبلغ
const DonutChart = ({ data, title, icon, getValue, formatValue }) => {
    const total = data.reduce((sum, item) => sum + Math.max(0, getValue(item)), 0);
    // 👈 افزایش تعداد رنگ‌ها برای جلوگیری از تکرار زودهنگام
    const colors = [
        '#2563eb', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4',
        '#ec4899', '#14b8a6', '#f43f5e', '#84cc16', '#eab308', '#6366f1'
    ];

    let currentOffset = 0;
    const radius = 50;
    const circumference = 2 * Math.PI * radius;

    return (
        <section className="vendor-report-chart vendor-report-chart--donut">
            <header className="vendor-report-chart__header">
                {icon}
                <h3>{title}</h3>
            </header>
            {data.length === 0 ? (
                <p className="vendor-report__empty">هنوز فروشی ثبت نشده است.</p>
            ) : (
                <div className="donut-chart-container">
                    <div className="donut-chart-wrapper">
                        <svg viewBox="0 0 120 120" className="donut-chart">
                            <circle cx="60" cy="60" r={radius} fill="transparent" stroke="#f1f5f9" strokeWidth="16" />
                            {data.map((item, index) => {
                                const value = Math.max(0, getValue(item));
                                if (value === 0) return null;

                                const percent = value / total;
                                const strokeLength = percent * circumference;
                                const strokeOffset = circumference - strokeLength;

                                const circle = (
                                    <circle
                                        key={item.package_id}
                                        cx="60"
                                        cy="60"
                                        r={radius}
                                        fill="transparent"
                                        stroke={colors[index % colors.length]}
                                        strokeWidth="16"
                                        strokeDasharray={circumference}
                                        strokeDashoffset={strokeOffset}
                                        transform={`rotate(${(currentOffset / circumference) * 360 - 90} 60 60)`}
                                        className="donut-segment"
                                    />
                                );
                                currentOffset += strokeLength;
                                return circle;
                            })}
                        </svg>
                        <div className="donut-chart-center">
                            <span className="donut-chart-total-label">کل درآمد</span>
                            <strong className="donut-chart-total-value">{formatValue(total)}</strong>
                        </div>
                    </div>
                    <ul className="donut-chart-legend">
                        {data.map((item, index) => (
                            <li key={item.package_id} className="donut-legend-item">
                                <span className="donut-legend-color" style={{ backgroundColor: colors[index % colors.length] }} />
                                {/* 👈 [CHANGED] فقط نام طرح نمایش داده می‌شود */}
                                <span className="donut-legend-label">{getPackageDisplayName(item.package_name)}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
};

const MetricCard = ({ icon: Icon, label, value, unit, secondary, tone = 'default' }) => (
    <article className={`vendor-report-metric vendor-report-metric--${tone}`}>
        <div className="vendor-report-metric__label">
            <Icon size={17} aria-hidden="true" />
            <span>{label}</span>
        </div>
        <strong className="vendor-report-metric__value">
            {value}
            {unit && <span className="vendor-report-metric__unit">{unit}</span>}
        </strong>
        {secondary && <span className="vendor-report-metric__secondary">{secondary}</span>}
    </article>
);

const formatPhone = (value) => String(value ?? '').replace(/\d/g, (digit) => '۰۱۲۳۴۵۶۷۸۹'[Number(digit)]);

const createExportRow = (pkg, metrics) => ({
    'نام پکیج': getPackageDisplayName(pkg.package_name),
    'تعداد سفارش‌ها': metrics.orders,
    'تعداد فروش': Number(pkg.sales_count ?? metrics.orders),
    'خریداران': Array.isArray(pkg.buyers) ? pkg.buyers.length : 0,
    'درآمد (ریال)': metrics.revenue,
    'کل فروش (ریال)': Number(pkg.total_sales ?? metrics.revenue),
    'بهای تمام‌شده محصول (ریال)': metrics.productCost,
    'سود ناخالص (ریال)': metrics.grossProfit,
    'کارمزد پلتفرم (ریال)': metrics.platformCommission,
    'پورسانت معرفی پرداخت‌شده (ریال)': metrics.paidReferralCommission,
    'پورسانت پرداخت‌شده (ریال)': Number(pkg.paid_commission ?? metrics.paidReferralCommission),
    'پورسانت معرفی در انتظار (ریال)': metrics.pendingReferralCommission,
    'پورسانت در انتظار (ریال)': Number(pkg.pending_commission ?? metrics.pendingReferralCommission),
    'سود خالص (ریال)': metrics.netProfit,
    'سود به‌ازای هر سفارش (ریال)': metrics.profitPerOrder,
    'حاشیه سود (%)': metrics.marginPct,
});

export default function VendorReportsPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [expandedPackages, setExpandedPackages] = useState({});
    const navigate = useNavigate();
    const reportRef = useRef(null);

    useEffect(() => {
        let isMounted = true;
        const fetchReports = async () => {
            try {
                const response = await getVendorReports();
                if (isMounted) setData(response);
            } catch (requestError) {
                if (isMounted) setError(requestError.message);
            } finally {
                if (isMounted) setLoading(false);
            }
        };
        fetchReports();
        return () => {
            isMounted = false;
        };
    }, []);

    const packages = useMemo(
        () => (Array.isArray(data?.packages) ? data.packages : []),
        [data],
    );
    const summary = useMemo(
        () => getPlanProfitMetrics(data?.summary ?? {}),
        [data],
    );
    const averageOrderValue = summary.orders > 0 ? summary.revenue / summary.orders : null;

    const togglePackage = (packageId) => {
        setExpandedPackages((current) => ({
            ...current,
            [packageId]: !current[packageId],
        }));
    };

    const handleExportExcel = () => {
        if (!data) return;

        const summaryData = packages.map((pkg) => createExportRow(pkg, getPlanProfitMetrics(pkg)));
        const summaryRow = createExportRow({ package_name: 'جمع کل' }, summary);
        summaryRow['تعداد فروش'] = summary.orders;
        summaryRow['کل فروش (ریال)'] = summary.revenue;
        summaryRow['پورسانت پرداخت‌شده (ریال)'] = summary.paidReferralCommission;
        summaryRow['پورسانت در انتظار (ریال)'] = summary.pendingReferralCommission;
        summaryData.push(summaryRow);

        const buyersData = [];
        packages.forEach((pkg) => {
            (Array.isArray(pkg.buyers) ? pkg.buyers : []).forEach((buyer) => {
                buyersData.push({
                    'نام پکیج': getPackageDisplayName(pkg.package_name),
                    'نام خریدار': buyer.buyer_name,
                    'شماره تماس': formatPhone(buyer.buyer_mobile),
                    'معرف': buyer.referrer_name,
                    'مبلغ خرید (ریال)': buyer.amount,
                    'تاریخ': formatJalaliDate(buyer.date),
                });
            });
        });

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryData), 'خلاصه پکیج‌ها');
        XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(buyersData), 'جزئیات خریداران');

        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `گزارش_فروش_${exportDate()}.xlsx`);
    };

    const handleExportPDF = async () => {
        if (!reportRef.current) return;
        const previousExpanded = expandedPackages;
        setExpandedPackages(Object.fromEntries(packages.map((pkg) => [pkg.package_id, true])));
        await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
        try {
            await html2pdf()
                .set({
                    margin: 10,
                    filename: `گزارش_فروش_${exportDate()}.pdf`,
                    image: { type: 'jpeg', quality: 0.98 },
                    html2canvas: { scale: 2, useCORS: true },
                    jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
                })
                .from(reportRef.current)
                .save();
        } catch (exportError) {
            setError(`خروجی PDF ساخته نشد: ${exportError.message}`);
        } finally {
            setExpandedPackages(previousExpanded);
        }
    };

    if (loading) return <div className="vendor-report__status">در حال محاسبه گزارش‌ها...</div>;
    if (error) return <div className="vendor-report__error">{error}</div>;
    if (!data) return null;

    const primaryCards = [
        { label: 'درآمد', value: formatReportCurrency(summary.revenue), icon: TrendingUp, tone: 'blue' },
        { label: 'تعداد سفارش‌ها', value: formatReportCount(summary.orders), unit: 'سفارش', icon: PackageCheck, tone: 'violet' },
        { label: 'سود ناخالص', value: formatReportCurrency(summary.grossProfit), icon: BarChart3, tone: 'teal' },
        { label: 'سود خالص', value: formatReportCurrency(summary.netProfit), icon: Wallet, tone: 'green' },
    ];

    const secondaryCards = [
        { label: 'بهای تمام‌شده محصول', value: formatReportCurrency(summary.productCost), icon: PackageCheck },
        { label: 'کارمزد پلتفرم', value: formatReportCurrency(summary.platformCommission), icon: Activity },
        { label: 'پورسانت معرفی پرداخت‌شده', value: formatReportCurrency(summary.paidReferralCommission), icon: ArrowUpRight, tone: 'red' },
        { label: 'پورسانت معرفی در انتظار', value: formatReportCurrency(summary.pendingReferralCommission), icon: AlertCircle, tone: 'amber' },
        { label: 'سود به‌ازای هر سفارش', value: formatReportCurrency(summary.profitPerOrder), icon: Wallet },
        { label: 'حاشیه سود', value: formatReportPercent(summary.marginPct), icon: Activity, tone: 'green' },
        { label: 'میانگین ارزش سفارش', value: formatReportCurrency(averageOrderValue), icon: BarChart3 },
    ];

    return (
        <main className="vendor-report" dir="rtl">
            <header className="vendor-report__page-header">
                <div className="vendor-report__title">
                    <BarChart3 size={27} aria-hidden="true" />
                    <div><h1>گزارش جامع فروش پکیج‌ها</h1></div>
                </div>
                <div className="vendor-report__actions">
                    <button className="vendor-report__button vendor-report__button--excel" onClick={handleExportExcel}>
                        <Download size={16} aria-hidden="true" /> خروجی اکسل
                    </button>
                    <button className="vendor-report__button vendor-report__button--pdf" onClick={handleExportPDF}>
                        <FileText size={16} aria-hidden="true" /> خروجی PDF
                    </button>
                </div>
            </header>

            <div className="vendor-report__content" ref={reportRef}>
                <section className="vendor-report__summary" aria-labelledby="vendor-report-summary-title">
                    <div className="vendor-report__section-heading" style={{ marginBottom: '0.5rem' }}>
                        <h2 id="vendor-report-summary-title">خلاصه عملکرد</h2>
                    </div>
                    <p style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.35rem',
                        margin: '0 0 1.2rem 0', color: 'var(--report-muted)', fontSize: '0.78rem',
                        lineHeight: 1.8, textAlign: 'center'
                    }}>
                        <Info size={15} aria-hidden="true" />
                        سود خالص = {NET_PROFIT_FORMULA}
                    </p>

                    <div className="vendor-report__metrics vendor-report__metrics--primary">
                        {primaryCards.map((card) => <MetricCard key={card.label} {...card} />)}
                    </div>

                    <div className="vendor-report__metrics vendor-report__metrics--secondary">
                        {secondaryCards.map((card) => <MetricCard key={card.label} {...card} />)}
                    </div>
                </section>

                {/* 👈 [CHANGED] فقط نمودار دایره‌ای باقی مانده است */}
                {packages.length > 0 && (
                    <section className="vendor-report__charts" aria-label="نمودارهای عملکرد طرح‌ها">
                        <DonutChart
                            data={packages}
                            title="سهم درآمد هر طرح"
                            icon={<TrendingUp size={17} aria-hidden="true" />}
                            getValue={(pkg) => getPlanProfitMetrics(pkg).revenue}
                            formatValue={formatReportCurrency}
                        />
                    </section>
                )}

                <section className="vendor-report__table-section" aria-labelledby="vendor-report-plans-title">
                    <div className="vendor-report__table-heading">
                        <div>
                            <h2 id="vendor-report-plans-title">جزئیات به تفکیک پکیج</h2>
                            <p>برای مشاهده بهای تمام‌شده و جزئیات پورسانت، هر طرح را باز کنید.</p>
                        </div>
                    </div>
                    {packages.length === 0 ? (
                        <div className="vendor-report__empty vendor-report__empty--large">هنوز هیچ فروشی ثبت نشده است.</div>
                    ) : (
                        <table className="vendor-report__table">
                            <thead>
                            <tr>
                                <th scope="col">نام پکیج</th>
                                <th scope="col">سفارش‌ها</th>
                                <th scope="col">درآمد</th>
                                <th scope="col" title={NET_PROFIT_FORMULA}>سود خالص <Info size={13} aria-label={NET_PROFIT_FORMULA} /></th>
                                <th scope="col">حاشیه سود</th>
                                <th scope="col"><span className="vendor-report__visually-hidden">جزئیات</span></th>
                            </tr>
                            </thead>
                            <tbody>
                            {packages.map((pkg) => {
                                const metrics = getPlanProfitMetrics(pkg);
                                const isExpanded = Boolean(expandedPackages[pkg.package_id]);
                                const detailId = `package-report-${pkg.package_id}`;
                                return (
                                    <Fragment key={pkg.package_id}>
                                        <tr className="vendor-report__plan-row">
                                            <th scope="row" data-label="نام پکیج">{getPackageDisplayName(pkg.package_name)}</th>
                                            <td data-label="سفارش‌ها">{formatReportCount(metrics.orders)}</td>
                                            <td data-label="درآمد">{formatReportCurrency(metrics.revenue)}</td>
                                            <td data-label="سود خالص">
                                                <strong className="vendor-report__profit">{formatReportCurrency(metrics.netProfit)}</strong>
                                            </td>
                                            <td data-label="حاشیه سود">{formatReportPercent(metrics.marginPct)}</td>
                                            <td className="vendor-report__row-action">
                                                <button
                                                    className="vendor-report__details-button"
                                                    aria-expanded={isExpanded}
                                                    aria-controls={detailId}
                                                    onClick={() => togglePackage(pkg.package_id)}
                                                >
                                                    جزئیات <ChevronDown size={16} aria-hidden="true" />
                                                </button>
                                            </td>
                                        </tr>
                                        {isExpanded && (
                                            <tr className="vendor-report__details-row" id={detailId}>
                                                <td colSpan={6}>
                                                    {metrics.orders === 0 ? (
                                                        <p className="vendor-report__empty">برای این پکیج هنوز سفارشی ثبت نشده است.</p>
                                                    ) : (
                                                        <div className="vendor-report__details">
                                                            <DetailMetric label="بهای تمام‌شده محصول" value={formatReportCurrency(metrics.productCost)} />
                                                            <DetailMetric label="سود ناخالص" value={formatReportCurrency(metrics.grossProfit)} />
                                                            <DetailMetric label="کارمزد پلتفرم" value={formatReportCurrency(metrics.platformCommission)} />
                                                            <DetailMetric label="پورسانت معرفی پرداخت‌شده" value={formatReportCurrency(metrics.paidReferralCommission)} />
                                                            <DetailMetric label="پورسانت معرفی در انتظار" value={formatReportCurrency(metrics.pendingReferralCommission)} />
                                                            <DetailMetric label="سود به‌ازای هر سفارش" value={formatReportCurrency(metrics.profitPerOrder)} />
                                                        </div>
                                                    )}
                                                    <button className="vendor-report__buyers-button" onClick={() => navigate(`/vendor/reports/buyers/${pkg.package_id}`)}>
                                                        <Users size={15} aria-hidden="true" /> مشاهده خریداران
                                                    </button>
                                                </td>
                                            </tr>
                                        )}
                                    </Fragment>
                                );
                            })}
                            </tbody>
                        </table>
                    )}
                </section>
            </div>
        </main>
    );
}

const DetailMetric = ({ label, value }) => (
    <div className="vendor-report__detail-metric">
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);